import { FormEvent, FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import track, { errorTypes } from 'c9-tracking';
import { GiftCard, TdkAutoComplete, formatPrice } from '@flos/react-components';
import { LoginCheck } from '../../components/shared/login-check/LoginCheck';
import { checkLoggedInUser } from '../../components/shared/login-check/parts/customerChecks';
import { useStateValue } from '../../providers/StateProvider';
import { RenderHtml } from '../../components/RenderHtml/RenderHtml';
import { baseStyles } from '../../theme/base.styles';
import { ROUTE_NAMES } from '../../router/RouterApp';
import * as ActionKeys from '../../store/ActionKeys';
import Button from '@material-ui/core/Button';
import { IProps } from '../../interfaces/IProps';
import { CUSTOMER_STATUS, EMAIL_EVENT } from '../../interfaces/IEmailPayload';
import { fetchGiftCardDetails } from '../../store/Actions';
import { FLOW, flowConfig } from '../../interfaces/IPayload';
import { SET_IS_USER_CHECKED } from '../../store/ActionKeys';

const OccupationScreenView: FunctionComponent<IProps> = (props: IProps): JSX.Element => {
  const [
    { labels, occupation, occupations, contentBlocks, statusInitial, errorCode, giftCard, flow, isUserChecked },
    dispatch
  ] = useStateValue();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const baseClass = baseStyles();
  const [customerStatus, setCustomerStatus] = useState<CUSTOMER_STATUS>(CUSTOMER_STATUS.NOT_SET);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [init, setInit] = useState(true);
  // If splitTest = true, then a split est is going on - otherwise false
  const splitTest = true;

  const fetchGiftCard = useCallback(async () => {
    if (!giftCard && !isWaiting) {
      try {
        setIsWaiting(true);
        const response = await fetchGiftCardDetails(props.endpoint.giftCard, props.c9log);
        props.c9log.info(`gift card status for injury: ${response?.data.status}`, 'Occupation');
        track.info([{ key: 'gift card status for injury: ', value: response?.data.status }]);
        dispatch({ type: ActionKeys.SET_GIFT_CARD, payload: response?.data });
        setIsWaiting(false);
      } catch (e) {
        dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
        track.fault('get gift card status for car failed', errorTypes.tech, 'giftCard');
      }
    }
  }, [giftCard, props, dispatch, isWaiting]);

  useEffect(() => {
    // for split test
    if (init) {
      setInit(false);
      let _flow = FLOW.CURRENT;

      if (splitTest) {
        const index = Math.floor(Math.random() * flowConfig.length);
        _flow = flow === null || flow === FLOW.NOT_SET ? flowConfig.find((x) => x.index === index)?.flow : flow;
      }

      dispatch({ type: ActionKeys.SET_FLOW, payload: _flow });
      track.info(`Onsa-Injury-Flow, flow = ${_flow}`);
    }
  }, [dispatch, init, flow, splitTest]);

  useEffect(() => {
    // resets state if this page/step is reloaded
    if (statusInitial) {
      dispatch({ type: ActionKeys.SET_RESET, payload: null });
      // Must only be set at start up (i.e. browser refresh on this page)
      dispatch({ type: ActionKeys.SET_STATUSINITIAL, payload: false });
    }

    if (window.topContext?.user.isLoggedOn && !isUserChecked) {
      // user is logged in, start doing customer check
      dispatch({ type: SET_IS_USER_CHECKED, payload: true });
      (async () => {
        const loggedCustomerStatus = await checkLoggedInUser(props.endpoint, dispatch, props.c9log);
        track.info([
          { key: 'customer-status', value: loggedCustomerStatus },
          { key: 'customer-logged-on', value: 'Yes' }
        ]);
        setCustomerStatus(loggedCustomerStatus);
        if (loggedCustomerStatus !== 'PASS') {
          if (loggedCustomerStatus === CUSTOMER_STATUS.AGE_ABOVE_68) {
            dispatch({ type: ActionKeys.SET_RMO_REASON, payload: EMAIL_EVENT.AGE_ABOVE_68 });
            dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
          } else {
            setOverlayOpen(true);
          }
        }
      })();
    } else if (customerStatus === CUSTOMER_STATUS.NOT_SET && !window.topContext?.user.isLoggedOn) {
      // user is not logged in
      track.info([
        { key: 'customer-status', value: CUSTOMER_STATUS.PASS },
        { key: 'customer-logged-on', value: 'No' }
      ]);
      setCustomerStatus(CUSTOMER_STATUS.PASS);
    }
    fetchGiftCard();
  }, [dispatch, statusInitial, occupations, customerStatus, isUserChecked, props.endpoint, props.c9log, fetchGiftCard]);

  const occupationHandler = (_event: object, value: any, reason: string) => {
    if (reason === 'create-option' || reason === 'select-option') {
      setIsSubmitted(false);
    }
    dispatch({ type: ActionKeys.SET_OCCUPATION, payload: value });
  };

  const rmoHandler = () => {
    dispatch({ type: ActionKeys.SET_RMO_REASON, payload: EMAIL_EVENT.OCCUPATION_NOT_SUPPORTED });
    dispatch({ type: ActionKeys.SET_HAS_ERROR, payload: { stepOne: true, stepTwo: false } });
    track.fault('Occupation not in list"-button clicked', errorTypes.user);
  };

  const submitHandler = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitted(true);
    if (occupation && occupation.text !== '') {
      track.form('onsa-personal-injury-form-occupation', [{ id: 'occupation', value: occupation.text }]);
      dispatch({ type: ActionKeys.SET_RESET_ERROR, payload: {} });
      dispatch({ type: ActionKeys.SET_STATE_CHANGE, payload: true });
      track.info([{ key: 'customer status', value: customerStatus }]);
      if (window.topContext?.user.isLoggedOn && customerStatus === CUSTOMER_STATUS.PASS) {
        navigate(ROUTE_NAMES.POLICYHOLDER);
      } else if (customerStatus === CUSTOMER_STATUS.PASS) {
        navigate(ROUTE_NAMES.CUSTOMER_OR_NOT);
      }
    }
  };

  const onDialogClose = () => {
    setOverlayOpen(false);
    if (customerStatus !== CUSTOMER_STATUS.PASS) {
      window.location.assign(window.location.origin + '/forsikringer/ulykkesforsikring/');
    }
  };

  if (customerStatus === CUSTOMER_STATUS.PASS && giftCard) {
    const constructHeader = () => {
      if (giftCard?.status === 'active' && contentBlocks.giftcardIntro) {
        return (
          <>
            <RenderHtml>
              {contentBlocks.giftcardIntro.replace('{amount}', formatPrice(giftCard.amount.toString()))}
            </RenderHtml>
            {giftCard?.status === 'active' && (
              <GiftCard
                productType="PERSONAL_INJURY"
                amount={formatPrice(giftCard.amount.toString())}
                campaign={giftCard.campaign}
              />
            )}
            <Typography variant="subtitle1" color="textPrimary" className={baseClass.spacingNormal} paragraph>
              {labels.occupation.intro.label}
            </Typography>
          </>
        );
      } else {
        return <RenderHtml>{contentBlocks.intro}</RenderHtml>;
      }
    };

    return (
      <Grid container spacing={1} className={baseClass.main}>
        <Grid item xs={12}>
          <Typography variant="h1" color="textPrimary" className={baseClass.h1}>
            {labels.occupation.header.label}
          </Typography>

          <Typography variant="subtitle1" className={baseClass.flosIcon} color="textPrimary">
            <flos-icon shape="avatar-colored" size="40" style={{ padding: '0 8px' }} class="flos-icon" />
          </Typography>

          <>{constructHeader()}</>

          <form onSubmit={submitHandler} noValidate={true} name="onsa-personal-injury-occupation">
            <TdkAutoComplete
              customClass={baseClass.spacingNormal}
              label={labels.occupation.list.placeholder}
              id={`tracking-occupation`}
              options={occupations}
              groupBy={(option) => (option.category === 'Mest valgte' ? 'Mest valgte' : 'Alfabetisk')}
              getOptionLabel={(option) => option.text}
              highlight={true}
              highlightKey={'text'}
              isSubmitted={isSubmitted}
              startShowing={2}
              showOnFocus={flow === FLOW.DROP_DOWN_ON_FOCUS}
              noOptionsText={'Intet fundet'}
              onChange={occupationHandler}
              value={occupation}
              getOptionSelected={(option) => option.numValue === occupation.numValue}
              data-testid="occupation"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitted}
              className={baseClass.spacingNormal}
            >
              {labels.button.continue}
            </Button>

            <Button type="button" fullWidth variant="outlined" onClick={rmoHandler}>
              {labels.button.occupation.notlisted}
            </Button>
          </form>
        </Grid>
      </Grid>
    );
  } else if (customerStatus === CUSTOMER_STATUS.NOT_SET || errorCode) {
    return <></>;
  }

  return (
    <LoginCheck overlayOpen={overlayOpen} customerStatus={customerStatus} onDialogClose={onDialogClose} full={true} />
  );
};

export default OccupationScreenView;
